/**
 * Phase 2h — Bastet-defeated cutscene.
 *
 * Full-viewport overlay shown ONCE, on the first run where the player kills
 * Bastet. App.tsx mounts this by comparing the freshly-returned `bossDefeated`
 * against the cached achievements snapshot taken BEFORE the submit-run round-
 * trip. Pressing the continue button dismisses it and (in App.tsx) also clears
 * `reactorMode` so the next ▶ launch is a regular run.
 *
 * Styling follows the Claude Design warm-gold palette established in Phase 2a:
 * Russo One headline, Manrope subtitle, gold-rim chunk for the rewards list,
 * `.cta.gold` continue button. Inline styles match the BastetDialogOverlay
 * pattern from Task 14. All copy is keyed through i18n (RU is the shipping
 * language; EN strings ship as fallback for future localisation).
 */

import { useEffect } from 'react';
import { useT } from '../i18n';
import { sfxCodexUnlock } from '../game/audio';

type Props = { onClose: () => void };

export function BastetDefeatedScreen({ onClose }: Props) {
  const { t } = useT();
  // Single short "data ping" announcing the 3 codex entries that just unlocked
  // (bastet_corrupted / reactor_origin / soul_eater). Mounted once per first-
  // time Bastet defeat — App.tsx gates the mount on freshly-unlocked state.
  useEffect(() => {
    sfxCodexUnlock();
  }, []);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('bastet.defeated.title')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background:
          'radial-gradient(60% 40% at 50% 30%, rgba(245,197,66,0.18), rgba(0,0,0,0.92))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 28,
        padding: 32,
      }}
    >
      <div
        style={{
          fontFamily: '"Russo One", sans-serif',
          fontSize: 36,
          letterSpacing: '0.06em',
          color: '#ffd866',
          textShadow:
            '0 0 24px rgba(245,197,66,0.6), 0 1px 0 rgba(0,0,0,0.5)',
          textAlign: 'center',
          lineHeight: 1.1,
          whiteSpace: 'pre-line',
        }}
      >
        {t('bastet.defeated.title')}
      </div>

      <div
        style={{
          fontFamily: '"Manrope", sans-serif',
          fontSize: 14,
          color: 'rgba(255,255,255,0.72)',
          letterSpacing: '0.06em',
          textAlign: 'center',
          lineHeight: 1.5,
          maxWidth: 360,
          whiteSpace: 'pre-line',
        }}
      >
        {t('bastet.defeated.subtitle')}
      </div>

      <div
        style={{
          padding: '12px 18px',
          background: 'rgba(245,197,66,0.08)',
          border: '1px solid rgba(245,197,66,0.35)',
          borderRadius: 12,
          boxShadow: 'inset 0 0 24px rgba(245,197,66,0.08)',
          fontFamily: '"Russo One", sans-serif',
          fontSize: 11,
          letterSpacing: '0.14em',
          color: '#ffd866',
          textAlign: 'center',
          lineHeight: 1.7,
          whiteSpace: 'pre-line',
        }}
      >
        {t('bastet.defeated.unlocks')}
      </div>

      <button
        type="button"
        onClick={onClose}
        className="cta gold"
        style={{ marginTop: 12, minWidth: 200 }}
      >
        {t('bastet.defeated.continue')}
      </button>
    </div>
  );
}
